import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';

import {
  useStripeTopUpControllerCatalogQuery,
  useStripeTopUpControllerCheckoutMutation,
  type BillingTopUpOfferDto,
  type CompanyBillingSummaryDto,
} from '@shared/services/apiService';

import { billingErrorMessage, hostedBillingUrl } from '../model/billingError';
import { formatBillingMoney, formatCreditUnits } from '../model/formatBillingUnits';

import { BillingCreditBalance } from './BillingCreditBalance';
import { BillingPaymentHistory } from './BillingPaymentHistory';

export const BillingTopUps = ({
  companyId,
  onRefresh,
  refreshSignal = 0,
  summary,
  availableUnits = '0',
  actions,
  overview,
}: {
  companyId: string;
  onRefresh: () => void;
  refreshSignal?: number;
  summary?: CompanyBillingSummaryDto;
  availableUnits?: string;
  actions?: ReactNode;
  overview?: ReactNode;
}) => {
  const catalog = useStripeTopUpControllerCatalogQuery(
    { companyId },
    { refetchOnMountOrArgChange: true },
  );
  const { refetch } = catalog;
  const lastRefreshSignal = useRef(refreshSignal);
  useEffect(() => {
    if (lastRefreshSignal.current !== refreshSignal) {
      lastRefreshSignal.current = refreshSignal;
      void refetch();
    }
  }, [refreshSignal, refetch]);
  const [checkout, { isLoading }] = useStripeTopUpControllerCheckoutMutation();
  const [search] = useSearchParams();
  const [choosing, setChoosing] = useState(false);
  const [selected, setSelected] = useState<BillingTopUpOfferDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const requestKeys = useRef(new Map<string, string>());
  const active = useRef(false);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  useEffect(() => {
    if (
      catalog.data &&
      !catalog.data.purchases.some((p) =>
        ['prepared', 'sending', 'unknown', 'open', 'pending'].includes(p.state),
      )
    )
      requestKeys.current.clear();
  }, [catalog.data]);
  const returning = search.get('topup');
  const purchaseId = search.get('purchase');

  useEffect(() => {
    if (returning !== 'success') return;
    let count = 0;
    const timer = window.setInterval(() => {
      if (++count >= 8) window.clearInterval(timer);
      void refetch();
      onRefresh();
    }, 2500);
    return () => window.clearInterval(timer);
  }, [returning, companyId, refetch, onRefresh]);

  const pay = async (offer: BillingTopUpOfferDto) => {
    if (catalog.isError || !catalog.data?.canPurchase) return;
    setError(null);
    setMessage(null);
    const key = requestKeys.current.get(offer.id) ?? crypto.randomUUID();
    requestKeys.current.set(offer.id, key);
    try {
      const result = await checkout({
        companyId,
        stripeTopUpRequestDto: { offerId: offer.id, requestKey: key },
      }).unwrap();
      if (!active.current) return;
      setSelected(null);
      if (result.state === 'open' && result.url)
        window.location.assign(hostedBillingUrl(result.url, 'checkout'));
      else {
        setMessage(
          result.state === 'succeeded'
            ? 'Payment confirmed. Your top-up credits are available.'
            : result.state === 'expired'
              ? 'This checkout expired. You can choose a package again.'
              : 'Payment is being verified. Credits are added only after server confirmation.',
        );
        void refetch();
        onRefresh();
      }
    } catch (e) {
      if (active.current) setError(billingErrorMessage(e));
    }
  };

  const data = catalog.data;
  const confirmed =
    !!purchaseId && data?.purchases.some((p) => p.id === purchaseId && p.state === 'succeeded');
  const pending = data?.purchases.some((p) =>
    ['prepared', 'sending', 'unknown', 'open', 'pending'].includes(p.state),
  );
  const controls = (
    <Stack
      direction="row"
      alignItems="flex-start"
      spacing={1}
      useFlexGap
      flexWrap="wrap"
      sx={{ minWidth: 0, maxWidth: '100%' }}>
      <Button
        variant="contained"
        disabled={
          catalog.isError || !data?.canPurchase || isLoading || pending || !data.offers.length
        }
        onClick={() => {
          if (data?.offers.length === 1) setSelected(data.offers[0]);
          else setChoosing(true);
        }}>
        Add credits
      </Button>
      {actions}
    </Stack>
  );
  return (
    <Paper
      component="section"
      aria-label="Credit activity"
      variant="outlined"
      sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
      <Stack spacing={2}>
        {summary && (summary.plan || summary.previousPeriod) ? (
          <BillingCreditBalance
            summary={summary}
            availableUnits={availableUnits}
            compact
            actions={controls}
          />
        ) : (
          <Stack
            direction="row"
            justifyContent="space-between"
            spacing={2}
            useFlexGap
            flexWrap="wrap">
            <Typography variant="h6" component="h2">
              Credit balance
            </Typography>
            {controls}
          </Stack>
        )}
        {overview}
        <Divider />
        {catalog.isLoading && <CircularProgress aria-label="Loading purchase history" />}
        {(catalog.isError || (!catalog.isLoading && !data)) && (
          <Alert severity="error" action={<Button onClick={() => void refetch()}>Retry</Button>}>
            Top-up information could not be loaded.
          </Alert>
        )}
        {returning === 'success' && data && (
          <Alert severity={confirmed ? 'success' : 'info'}>
            {confirmed
              ? 'Payment confirmed. Your top-up credits have been added.'
              : 'Your checkout returned successfully. Waiting for the server to confirm payment; credits have not been assumed.'}
          </Alert>
        )}
        {returning === 'cancel' && (
          <Alert severity="info">
            Checkout was closed. No credits are added until payment is confirmed.
          </Alert>
        )}
        {message && <Alert severity="info">{message}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
        <BillingPaymentHistory
          companyId={companyId}
          purchases={data?.purchases ?? []}
          canResume={!catalog.isError && !!data?.canPurchase}
          resuming={isLoading}
          onResume={(purchase) => void pay({ ...purchase, id: purchase.offerId })}
          refreshSignal={refreshSignal}
        />
        {data && !data.activePaidPeriod && (
          <Typography variant="caption" color="text.secondary">
            Top-ups require an active paid period. Stored top-up credits are retained while access
            is inactive.
          </Typography>
        )}
        {data?.offers.length === 0 && (
          <Typography variant="caption" color="text.secondary">
            Top-up packages have not been configured yet.
          </Typography>
        )}
        {data?.activePaidPeriod && !data.checkoutEnabled && (
          <Typography variant="caption" color="text.secondary">
            New top-up purchases are currently paused.
          </Typography>
        )}
        {!!data?.balances.length && (
          <Box component="details" sx={{ typography: 'body2' }}>
            <Box component="summary" sx={{ cursor: 'pointer', color: 'primary.main' }}>
              Stored top-up balances
            </Box>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {data.balances.map((b) => (
                <Box key={`${b.creditScale}:${b.chargingPolicyVersion}`}>
                  <Typography variant="body2">
                    {formatCreditUnits(b.availableUnits, b.creditScale)} unreserved top-up credits ·{' '}
                    {formatCreditUnits(b.heldUnits, b.creditScale)} reserved ·{' '}
                    {formatCreditUnits(b.spentUnits, b.creditScale)} used
                  </Typography>
                  {!data.activePaidPeriod && (
                    <Typography variant="caption" color="text.secondary">
                      Stored for your next active paid period.
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </Stack>
      <Dialog
        open={choosing}
        onClose={() => setChoosing(false)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="top-up-packages-title">
        <DialogTitle id="top-up-packages-title">Add credits</DialogTitle>
        <DialogContent>
          <Stack spacing={1}>
            {data?.offers.map((offer) => (
              <Button
                key={offer.id}
                variant="outlined"
                disabled={catalog.isError || !data.canPurchase || isLoading || pending}
                onClick={() => {
                  setChoosing(false);
                  setSelected(offer);
                }}>
                {offer.title} · {formatCreditUnits(offer.units, offer.creditScale)} credits ·{' '}
                {formatBillingMoney(offer.amountMinor, offer.currency, offer.currencyScale)}
              </Button>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChoosing(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!selected}
        onClose={() => {
          if (!isLoading) setSelected(null);
        }}
        fullWidth
        maxWidth="sm">
        <DialogTitle>Confirm one-time top-up</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            {selected && (
              <Typography variant="h5">
                {formatCreditUnits(selected.units, selected.creditScale)} credits ·{' '}
                {formatBillingMoney(
                  selected.amountMinor,
                  selected.currency,
                  selected.currencyScale,
                )}
              </Typography>
            )}
            <Typography>
              These credits never expire. Your subscription, renewal date and concurrent call limit
              stay the same.
            </Typography>
            <Typography color="text.secondary">
              You will pay securely with Stripe. Credits are added after the server verifies payment
              and do not repay previous call overruns.
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={isLoading} onClick={() => setSelected(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={catalog.isError || !data?.canPurchase || isLoading}
            onClick={() => selected && void pay(selected)}>
            {isLoading ? 'Opening Stripe…' : 'Continue to Stripe'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};
